import React, { useState, useEffect } from 'react';
import { Camera, Search } from 'lucide-react';
import AdminNavbar from '../../components/layout/AdminNavbar';
import api from '../../api/axios';
import { toast } from 'react-hot-toast';

const SelfieAccessPage = () => {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const fetchEmployees = async () => {
        try {
            const res = await api.get('/api/admin/employees/all', { cache: true });
            setEmployees(res.data || []);
        } catch (error) {
            console.error('Failed to fetch employees:', error);
            toast.error('Failed to load employees.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        document.title = "Selfie Access | Savrion Technologies";
        fetchEmployees();
    }, []);

    const toggleSelfieAccess = async (employeeId) => {
        // Optimistic UI update for 60 FPS instant response
        setEmployees(prev => prev.map(emp => emp._id === employeeId ? { ...emp, selfieEnabled: !(emp.selfieEnabled !== false) } : emp));

        try {
            const res = await api.patch(`/api/admin/employees/${employeeId}/toggle-selfie`);
            if (res.data) {
                setEmployees(prev => prev.map(emp => emp._id === employeeId ? { ...emp, selfieEnabled: res.data.selfieEnabled } : emp));
                toast.success(res.data.selfieEnabled ? 'Selfie access enabled' : 'Selfie access disabled');
            }
        } catch (error) {
            // Revert optimistic update on failure
            setEmployees(prev => prev.map(emp => emp._id === employeeId ? { ...emp, selfieEnabled: !(emp.selfieEnabled !== false) } : emp));
            console.error('Failed to toggle selfie access:', error);
            toast.error(error.response?.data?.message || 'Failed to toggle selfie access');
        }
    };

    const filteredEmployees = employees.filter(emp => 
        emp.name?.toLowerCase().includes(search.toLowerCase()) || 
        emp.employeeId?.toLowerCase().includes(search.toLowerCase()) ||
        emp.email?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="flex flex-col xl:flex-row min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
            <AdminNavbar />
            <main className="flex-1 min-w-0 w-full p-3 sm:p-4 lg:p-5 space-y-6 overflow-x-hidden">
                <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl p-4 shadow-[var(--shadow-soft)]">
                    <div className="flex items-center gap-4 min-w-0">
                        <div className="w-12 h-12 shrink-0 rounded-2xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] flex items-center justify-center">
                            <Camera size={24} />
                        </div>
                        <div>
                            <h1 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900 dark:text-white">
                                Selfie Access Control
                            </h1>
                            <p className="mt-1 text-sm text-[var(--text-secondary)]">
                                Manage which employees are allowed to use Selfie Attendance.
                            </p>
                        </div>
                    </div>
                    
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search employees..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10 pr-4 h-11 w-full sm:w-64 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] text-sm font-semibold outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] transition-all"
                        />
                    </div>
                </header>

                <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl shadow-[var(--shadow-soft)] overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-[var(--bg-secondary)] border-b border-[var(--border-color)]">
                                    <th className="p-4 text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)]">Employee</th>
                                    <th className="p-4 text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)]">ID</th>
                                    <th className="p-4 text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)]">Team & Role</th>
                                    <th className="p-4 text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)] text-right">Selfie Access</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--border-color)]">
                                {loading && employees.length === 0 ? (
                                    Array.from({ length: 5 }).map((_, idx) => (
                                        <tr key={`skeleton-${idx}`} className="animate-pulse">
                                            <td className="p-4"><div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded mb-1" /><div className="h-3 w-40 bg-slate-200 dark:bg-slate-700 rounded" /></td>
                                            <td className="p-4"><div className="h-4 w-16 bg-slate-200 dark:bg-slate-700 rounded" /></td>
                                            <td className="p-4"><div className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded mb-1" /><div className="h-3 w-16 bg-slate-200 dark:bg-slate-700 rounded" /></td>
                                            <td className="p-4 text-right"><div className="h-6 w-11 bg-slate-200 dark:bg-slate-700 rounded-full ml-auto" /></td>
                                        </tr>
                                    ))
                                ) : filteredEmployees.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="p-8 text-center text-[var(--text-secondary)] font-semibold">
                                            No employees found.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredEmployees.map(emp => (
                                        <tr key={emp._id} className="hover:bg-[var(--bg-secondary)]/50 transition-colors">
                                            <td className="p-4">
                                                <div className="font-bold text-[var(--text-primary)]">{emp.name}</div>
                                                <div className="text-xs font-semibold text-[var(--text-secondary)]">{emp.email}</div>
                                            </td>
                                            <td className="p-4 text-sm font-bold text-[var(--text-secondary)]">{emp.employeeId}</td>
                                            <td className="p-4 text-sm">
                                                <div className="font-bold text-[var(--text-primary)] capitalize">{emp.team}</div>
                                                <div className="text-xs font-semibold text-[var(--text-secondary)] capitalize">{emp.role}</div>
                                            </td>
                                            <td className="p-4 text-right">
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        className="sr-only peer"
                                                        checked={emp.selfieEnabled !== false}
                                                        onChange={() => toggleSelfieAccess(emp._id)}
                                                    />
                                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-[var(--card-bg)] after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-[var(--accent-primary)]"></div>
                                                    <span className={`ml-3 text-sm font-bold w-16 text-left ${emp.selfieEnabled !== false ? 'text-[var(--accent-primary)]' : 'text-[var(--text-secondary)]'}`}>
                                                        {emp.selfieEnabled !== false ? 'Enabled' : 'Disabled'}
                                                    </span>
                                                </label>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default SelfieAccessPage;


